import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, updateProfile, AuthError, sendEmailVerification } from 'firebase/auth'
import { IoLogoGoogle } from 'react-icons/io5'
import { auth, db} from 'src/config/firebaseConfig'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { Bounce, ToastContainer, toast } from 'react-toastify';


export const Signup = () => {
  const navigate = useNavigate();

  const [signingUp, setSigningUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Helper function to sanitize user inputs
  const sanitizeInput = (input: string): string => {
    return input.trim().replace(/[<>]/g, '');
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error: AuthError): string => {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use a stronger password.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/popup-closed-by-user':
        return 'Sign-up cancelled.';
      case 'auth/popup-blocked':
        return 'Popup was blocked. Please allow popups for this site.';
      default:
        return 'An error occurred during sign-up. Please try again.';
    }
  };

  const signUpWithGoogle = async () => {
    setSigningUp(true);
    setError('');

    try {
      const response = await signInWithPopup(auth, new GoogleAuthProvider());

      const userData = {
        name: response.user.displayName || '',
        email: response.user.email || '',
        phone: response.user.phoneNumber || '',
        street: '',
        state: '',
        city: '',
        postalCode: '',
        birthDay: null,
        role: "user",
        verified: response.user.emailVerified,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      await setDoc(
        doc(db, 'users', response.user.uid),
        userData,
      )

      toast.success('🎉 Account Created!', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      })
      navigate('/')
    } catch (err) {
      const error = err as AuthError;
      console.error('Google sign-up error:', error.code);
      setError(getErrorMessage(error));
      setSigningUp(false);
    }
  }

  const signUpWithEmail = async () => {
    setSigningUp(true)
    setError('')
    
    if (!formValidator()) {
      setSigningUp(false)
      return;
    }

    // Sanitize inputs
    const sanitizedEmail = email.trim().toLowerCase();
    const sanitizedFirstName = sanitizeInput(firstName);
    const sanitizedMiddleName = sanitizeInput(middleName);
    const sanitizedLastName = sanitizeInput(lastName);
    const sanitizedPhone = sanitizeInput(phone);
    const sanitizedStreet = sanitizeInput(street);
    const sanitizedCity = sanitizeInput(city);
    const sanitizedState = sanitizeInput(state);
    const sanitizedPostalCode = sanitizeInput(postalCode);

    try {
      const response = await createUserWithEmailAndPassword(auth, sanitizedEmail, password);
      console.log("User created: ", response.user.uid)

      await updateProfile(response.user, {
        displayName: `${sanitizedFirstName} ${sanitizedLastName}`
      })

      // Send email verification
      await sendEmailVerification(response.user);
    
      const bday = new Date(`${birthMonth}/${birthDay}/${birthYear}`);
      const userData = {
        name: `${sanitizedFirstName} ${sanitizedMiddleName} ${sanitizedLastName}`.trim(),
        email: sanitizedEmail,
        phone: sanitizedPhone,
        street: sanitizedStreet,
        state: sanitizedState,
        city: sanitizedCity,
        postalCode: sanitizedPostalCode,
        birthDay: bday,
        role: "user",
        verified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      // add user to firestore
      await setDoc(
        doc(db, 'users', response.user.uid),
        userData
      )

      toast.success('🎉 Account Created! Please verify your email.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      })

      navigate('/');
    } catch (err) {
      const error = err as AuthError;
      console.error('Email sign-up error:', error.code);
      setError(getErrorMessage(error));
      setSigningUp(false);
    }
  }

  const signUpValidator = () => {
    // Password requirements: at least 8 characters, one letter, one number
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (step === 1) {
      if (!trimmedEmail || !trimmedPassword || !trimmedConfirmPassword) {
        setError('Please fill in all fields');
        return;
      }
      
      if (!emailRegex.test(trimmedEmail)) {
        setError('Invalid email address format');
        return;
      }
      
      if (trimmedPassword !== trimmedConfirmPassword) {
        setError("Passwords do not match");
        return;
      }
      
      if (!passwordRegex.test(trimmedPassword)) {
        setError('Password must be at least 8 characters long, contain at least one letter and one number');
        return;
      }
      
      setError('');
      setStep(2);
    }
  }

  const formValidator = () => {
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !phone.trim() ||
      !street.trim() ||
      !state.trim() ||
      !city.trim() ||
      !postalCode.trim() ||
      !birthDay ||
      birthDay === 'Day' ||
      !birthMonth ||
      birthMonth === 'Month' ||
      !birthYear ||
      birthYear === 'Year'
    ) {
      setError('Please fill in all required fields');
      return false;
    }

    // Validate phone number format (basic validation)
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(phone)) {
      setError('Please enter a valid phone number');
      return false;
    }

    // Validate birth date
    const selectedDate = new Date(`${birthMonth}/${birthDay}/${birthYear}`);
    const today = new Date();
    const age = today.getFullYear() - selectedDate.getFullYear();
    
    if (age < 13) {
      setError('You must be at least 13 years old to create an account');
      return false;
    }

    if (selectedDate > today) {
      setError('Birth date cannot be in the future');
      return false;
    }

    return true;
  }   
  
  return (
    <div className='bg-gradient-to-br from-yellow-50 via-white to-gray-50 w-full min-h-screen flex items-center justify-center p-4'>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
      
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${step === 1 ? 'text-yellow-600' : 'text-gray-400'}`}>
                Account
              </span>
              <span className={`text-sm font-medium ${step === 2 ? 'text-yellow-600' : 'text-gray-400'}`}>
                Personal Info
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-2 rounded-full transition-all duration-500"
                style={{ width: step === 1 ? '50%' : '100%' }}
              ></div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-zinc-800 mb-2">Create Account</h1>
            <p className="text-gray-600 text-sm">
              {step === 1 ? 'Enter your email and password to get started' : 'Complete your profile information'}
            </p>
          </div>

          {/* step 1 */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center text-xl font-semibold leading-7 hidden">Sign In</div>
              <div className="text-center text-base font-normal">Enter your email and password to create your account</div>
              <input 
              type="text"
              placeholder='Email'
              className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex'
              value={email}
              onChange={e => setEmail(e.target.value)}
              />
              <div className="relative w-full">
                <input 
                type={showPassword ? 'text' : 'password'}
                placeholder='Password'
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex'
                value={password}
                onChange={e => setPassword(e.target.value)}
                />
                <button
                type='button'
                className='absolute right-4 top-2.5'
                onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <IoEyeOffOutline size={15}/> : <IoEyeOutline size={15}/>}
                </button>
              </div>
              <div className="relative w-full">
                <input 
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder='Confirm Password'
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex'
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                />
                <button
                type='button'
                className='absolute right-4 top-2.5'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <IoEyeOffOutline size={15}/> : <IoEyeOutline size={15}/>}
                </button>
              </div>
              <button 
              className='px-4 py-2 bg-zinc-800 text-slate-50 rounded-md justify-center items-center w-full'
              onClick={signUpValidator}
              disabled={signingUp}
              >
                Sign In with Email
              </button>
              
              {/* Display error message */}
              {error && <div className='text-red-500 text-xs'>{error}</div>}
              <div className="text-xs font-normal">OR CONTINUE WITH</div>
              <button 
              className='flex items-center justify-center w-full px-4 py-2 bg-zinc-800 text-slate-50 rounded-md gap-2.5'
              onClick={signUpWithGoogle}
              disabled={signingUp}
              >
                <IoLogoGoogle />
                Sign In with Google
              </button>
              <div className="flex gap-2.5 items-center justify-center w-fit text-zinc-800">
                Already have an account? <a href="/login" className='underline'>Log In</a>
              </div>
            </div>
          )}

          {/* step 2 */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center text-xl font-semibold leading-7">Sign In</div>
              <div className="text-center text-sm font-normal w-full flex">Personal Information</div>
              <div className="flex flex-row gap-2.5">
                <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='First Name'
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                />
                <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='Middle Name (optional)'
                value={middleName}
                onChange={e => setMiddleName(e.target.value)}
                />
              </div>
              <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='Last Name'
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                />
              {/* birthday component here */}
              <div className="flex flex-row gap-2.5 justify-around w-full">
                <select
                  className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex'
                  value={birthDay}
                  onChange={e => setBirthDay(e.target.value)}
                >
                  <option value="">Day</option>
                  {[...Array(31).keys()].map(day => (
                    <option key={day + 1} value={day + 1}>{day + 1}</option>
                  ))}
                </select>
                <select
                  className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex'
                  value={birthMonth}
                  onChange={e => setBirthMonth(e.target.value)}
                >
                  <option value="">Month</option>
                  {[
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                  ].map((month, index) => (
                    <option key={index + 1} value={index + 1}>{month}</option>
                  ))}
                </select>
                <select
                  className='w-full px-4 py-2 rounded-md border inline-flex'
                  value={birthYear}
                  onChange={e => setBirthYear(e.target.value)}
                >
                  <option value="">Year</option>
                  {[...Array(100).keys()].map(year => (
                    <option key={year + 1950} value={year + 1950}>{year + 1950}</option>
                  ))}
                </select>
              </div>
              <div className="text-center text-sm font-normal w-full flex">Contact Details</div>
              <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='Phone Number'
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
              <div className="text-center text-sm font-normal w-full flex">Address</div>
              <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='Street Address'
                value={street}
                onChange={e => setStreet(e.target.value)}
              />
              <div className="flex flex-row gap-2.5">
                <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='City'
                value={city}
                onChange={e => setCity(e.target.value)}
                />
                <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='State / Province'
                value={state}
                onChange={e => setState(e.target.value)}
                />
              </div>
              <input
                className='w-full px-4 py-2 rounded-md border justify-start items-center inline-flex' 
                type="text" 
                placeholder='Postal / Zip Code'
                value={postalCode}
                onChange={e => setPostalCode(e.target.value)}
              />
              <button 
              className={`px-4 py-2 rounded-md justify-center items-center w-full ${signingUp ? 'bg-gray-300' : 'bg-zinc-800 text-slate-50'}`}
              disabled={signingUp}
              onClick={signUpWithEmail}
              >
                {signingUp ? 'Signing Up...' : 'Sign Up'}
              </button>
              {error && <div className='text-red-500 text-xs'>{error}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}